import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import {
  REGEX_CP,
  REGEX_CURP,
  REGEX_RFC,
  type RepresentanteBorrador,
  type SepomexEntrada,
} from "@/pages/mi-registro-legal/types";

/**
 * Campos compartidos para captura/edición de Representante Legal. Reutilizado
 * por el wizard del cliente (Paso 3) y por el modal de edición desde el
 * detalle de Calidad. Lleva su propia cascada SEPOMEX.
 */
export function RepresentanteFormFields({
  r,
  setR,
}: {
  r: RepresentanteBorrador;
  setR: (next: RepresentanteBorrador) => void;
}) {
  const cp = (r.domicilioCodigoPostal ?? "").trim();
  const qSepomex = useQuery<SepomexEntrada[]>({
    queryKey: ["sepomex-rep", cp],
    enabled: REGEX_CP.test(cp),
    queryFn: async () => {
      const { data } = await api.get<SepomexEntrada[]>(`/api/sepomex`, { params: { cp } });
      return data;
    },
  });

  useEffect(() => {
    const entradas = qSepomex.data;
    if (!entradas || entradas.length === 0) return;
    const colActual = (r.domicilioColonia ?? "").trim();
    const haMatch = entradas.some((e) => e.asentamiento === colActual);
    if (r.domicilioEntidadFederativa && haMatch) return;
    const primera = entradas[0];
    setR({
      ...r,
      domicilioEntidadFederativa:
        r.domicilioEntidadFederativa && r.domicilioEntidadFederativa.trim()
          ? r.domicilioEntidadFederativa
          : primera.estado,
      domicilioMunicipio:
        r.domicilioMunicipio && r.domicilioMunicipio.trim()
          ? r.domicilioMunicipio
          : primera.municipio,
      domicilioColonia: haMatch ? colActual : primera.asentamiento,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qSepomex.data]);

  const colonias = qSepomex.data ?? [];
  const sinResultadosCP = REGEX_CP.test(cp) && qSepomex.isFetched && colonias.length === 0;

  return (
    <div className="space-y-5">
      <Bloque titulo="Datos personales">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Nombre(s) *</Label>
            <input
              type="text"
              required
              maxLength={120}
              value={r.nombres ?? ""}
              onChange={(e) => setR({ ...r, nombres: e.target.value })}
              className="w-full rounded border px-2 py-1.5 text-sm uppercase"
            />
          </div>
          <div>
            <Label>Apellido paterno *</Label>
            <input
              type="text"
              required
              maxLength={120}
              value={r.apellidoPaterno ?? ""}
              onChange={(e) => setR({ ...r, apellidoPaterno: e.target.value })}
              className="w-full rounded border px-2 py-1.5 text-sm uppercase"
            />
          </div>
          <div>
            <Label>Apellido materno</Label>
            <input
              type="text"
              maxLength={120}
              value={r.apellidoMaterno ?? ""}
              onChange={(e) => setR({ ...r, apellidoMaterno: e.target.value })}
              className="w-full rounded border px-2 py-1.5 text-sm uppercase"
            />
          </div>
          <div>
            <Label>RFC</Label>
            <input
              type="text"
              maxLength={13}
              value={r.rfc ?? ""}
              onChange={(e) => setR({ ...r, rfc: e.target.value.toUpperCase() })}
              className="w-full rounded border px-2 py-1.5 font-mono text-sm uppercase"
            />
          </div>
          <div>
            <Label>CURP</Label>
            <input
              type="text"
              maxLength={18}
              value={r.curp ?? ""}
              onChange={(e) => setR({ ...r, curp: e.target.value.toUpperCase() })}
              className="w-full rounded border px-2 py-1.5 font-mono text-sm uppercase"
            />
          </div>
        </div>
      </Bloque>

      <Bloque titulo="Domicilio">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Código postal</Label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={cp}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                setR({
                  ...r,
                  domicilioCodigoPostal: val,
                  ...(val.length < 5
                    ? { domicilioEntidadFederativa: "", domicilioMunicipio: "", domicilioColonia: "" }
                    : {}),
                });
              }}
              className="w-full rounded border px-2 py-1.5 font-mono text-sm"
            />
            {qSepomex.isFetching && (
              <p className="mt-1 flex items-center gap-1 text-xs text-fg-subtle">
                <MapPin className="h-3 w-3" /> Buscando…
              </p>
            )}
            {sinResultadosCP && <p className="mt-1 text-xs text-estado-warn">Captura manualmente.</p>}
          </div>
          <div>
            <Label>Entidad federativa</Label>
            <input
              type="text"
              value={r.domicilioEntidadFederativa ?? ""}
              onChange={(e) => setR({ ...r, domicilioEntidadFederativa: e.target.value })}
              disabled={colonias.length > 0}
              className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
            />
          </div>
          <div>
            <Label>Municipio / Alcaldía</Label>
            <input
              type="text"
              value={r.domicilioMunicipio ?? ""}
              onChange={(e) => setR({ ...r, domicilioMunicipio: e.target.value })}
              disabled={colonias.length > 0}
              className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
            />
          </div>
          <div className="sm:col-span-3">
            <Label>Colonia</Label>
            {colonias.length > 0 ? (
              <select
                value={r.domicilioColonia ?? ""}
                onChange={(e) => setR({ ...r, domicilioColonia: e.target.value })}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              >
                <option value="">— Selecciona colonia —</option>
                {colonias.map((c, i) => (
                  <option key={`${c.asentamiento}-${i}`} value={c.asentamiento}>
                    {c.asentamiento}
                    {c.tipoAsenta ? ` (${c.tipoAsenta})` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={r.domicilioColonia ?? ""}
                onChange={(e) => setR({ ...r, domicilioColonia: e.target.value })}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            )}
          </div>
          <div className="sm:col-span-3">
            <Label>Calle</Label>
            <input
              type="text"
              value={r.domicilioCalle ?? ""}
              onChange={(e) => setR({ ...r, domicilioCalle: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
          <div>
            <Label>Número exterior</Label>
            <input
              type="text"
              value={r.domicilioNumeroExterior ?? ""}
              onChange={(e) => setR({ ...r, domicilioNumeroExterior: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
          <div>
            <Label>Número interior</Label>
            <input
              type="text"
              value={r.domicilioNumeroInterior ?? ""}
              onChange={(e) => setR({ ...r, domicilioNumeroInterior: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
        </div>
      </Bloque>

      <Bloque titulo="Contacto">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Teléfono</Label>
            <input
              type="text"
              value={r.telefono ?? ""}
              onChange={(e) => setR({ ...r, telefono: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
          <div>
            <Label>Extensión</Label>
            <input
              type="text"
              maxLength={16}
              value={r.telefonoExtension ?? ""}
              onChange={(e) => setR({ ...r, telefonoExtension: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
          <div>
            <Label>Correo electrónico</Label>
            <input
              type="email"
              value={r.correo ?? ""}
              onChange={(e) => setR({ ...r, correo: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
        </div>
      </Bloque>

      <Bloque titulo="Instrumento de poder y vigencia">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Descripción del instrumento de poder</Label>
            <textarea
              rows={2}
              maxLength={500}
              value={r.instrumentoPoderDescripcion ?? ""}
              placeholder="Escritura, notaría, fecha. Ej.: Escritura 12345, Notaría 50 CDMX, 2024-03-15"
              onChange={(e) => setR({ ...r, instrumentoPoderDescripcion: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
          <div>
            <Label>Vigencia inicio</Label>
            <input
              type="date"
              value={(r.vigenciaInicio ?? "").substring(0, 10)}
              onChange={(e) =>
                setR({ ...r, vigenciaInicio: e.target.value ? `${e.target.value}T00:00:00Z` : null })
              }
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
          <div>
            <Label>Vigencia fin (opcional)</Label>
            <input
              type="date"
              value={(r.vigenciaFin ?? "").substring(0, 10)}
              onChange={(e) =>
                setR({ ...r, vigenciaFin: e.target.value ? `${e.target.value}T23:59:59Z` : null })
              }
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </div>
        </div>
      </Bloque>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={r.consentimientoNotificacionElectronica}
          onChange={(e) => setR({ ...r, consentimientoNotificacionElectronica: e.target.checked })}
          className="mt-0.5"
        />
        <span>Otorgo mi consentimiento para ser notificado vía correo electrónico.</span>
      </label>
    </div>
  );
}

/**
 * Devuelve un mensaje de error si la captura es inválida; null si OK.
 * Reglas: nombres + apellidoPaterno obligatorios; RFC/CURP/CP con regex si vienen.
 */
export function validarRepresentante(r: RepresentanteBorrador): string | null {
  const nombres = (r.nombres ?? "").trim();
  const ap = (r.apellidoPaterno ?? "").trim();
  if (!nombres || !ap) return "Captura al menos Nombre(s) y Apellido paterno.";

  const rfc = (r.rfc ?? "").trim().toUpperCase();
  if (rfc && !REGEX_RFC.test(rfc)) {
    return "RFC con formato inválido. Ej.: ABCD800101AB1 (12-13 chars).";
  }
  const curp = (r.curp ?? "").trim().toUpperCase();
  if (curp && !REGEX_CURP.test(curp)) {
    return "CURP con formato inválido. Ej.: ABCD800101HDFXYZ09.";
  }
  const cp = (r.domicilioCodigoPostal ?? "").trim();
  if (cp && !REGEX_CP.test(cp)) {
    return "Código postal debe tener 5 dígitos.";
  }
  return null;
}

/**
 * Normaliza el borrador para envío al backend (trim, upper/lower casing, null
 * en vez de strings vacíos). El backend deriva nombre_completo, así que aquí
 * se envía null.
 */
export function normalizarRepresentante(r: RepresentanteBorrador): RepresentanteBorrador {
  const cp = (r.domicilioCodigoPostal ?? "").trim();
  return {
    ...r,
    nombres: (r.nombres ?? "").trim() || null,
    apellidoPaterno: (r.apellidoPaterno ?? "").trim() || null,
    apellidoMaterno: (r.apellidoMaterno ?? "").trim() || null,
    nombreCompleto: null,
    rfc: ((r.rfc ?? "").trim().toUpperCase()) || null,
    curp: ((r.curp ?? "").trim().toUpperCase()) || null,
    correo: ((r.correo ?? "").trim().toLowerCase()) || null,
    telefono: (r.telefono ?? "").trim() || null,
    telefonoExtension: (r.telefonoExtension ?? "").trim() || null,
    domicilioCalle: (r.domicilioCalle ?? "").trim() || null,
    domicilioNumeroExterior: (r.domicilioNumeroExterior ?? "").trim() || null,
    domicilioNumeroInterior: (r.domicilioNumeroInterior ?? "").trim() || null,
    domicilioCodigoPostal: cp || null,
    domicilioColonia: (r.domicilioColonia ?? "").trim() || null,
    domicilioMunicipio: (r.domicilioMunicipio ?? "").trim() || null,
    domicilioEntidadFederativa: (r.domicilioEntidadFederativa ?? "").trim() || null,
    domicilioPais: (r.domicilioPais ?? "").trim() || "MEX",
    domicilioLibre: null,
    instrumentoPoderDescripcion: (r.instrumentoPoderDescripcion ?? "").trim() || null,
  };
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded border p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{titulo}</legend>
      {children}
    </fieldset>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{children}</div>;
}
